import { useCallback, useState } from 'react';
import { getAppConfig } from '../../../config';
import { loadCredentials, useApiClient } from '../../../apiClient';
import { UserHistoryItemKind } from '../../../models/payment/dto/user-history.dto';

/**
 * How long the object URL is kept alive after the click — the browser reads
 * the blob asynchronously, so revoking in the same tick can cancel or
 * truncate the download (same TTL/rationale as
 * `event-management/utils/exportEventAttendanceCsv.ts`'s `downloadCsv`).
 */
const OBJECT_URL_TTL_MS = 1000;

function downloadCsvText(csvText: string, filename: string): void {
  const blob = new Blob(['﻿' + csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
}

export type ExportUserHistoryCsvFilters = {
  kinds?: UserHistoryItemKind[];
  dateFrom?: string;
  dateTo?: string;
};

/**
 * Downloads the server-built CSV from `GET
 * /payments/admin/users/:userId/history/export` (epic M7 / T6@eng-review).
 * Not a `useQuery`/`fetchApi` call: that endpoint returns `text/csv`, not
 * JSON, and `ApiClient.fetchApi` unconditionally JSON-parses the response
 * body — so this does its own `fetch` (mirroring `apiClient.ts`'s internal
 * `invokeApi`, reusing its exported `loadCredentials`/`getAppConfig`)
 * reading `.text()` instead. Retries once through `forceTokenRefresh` on a
 * 401, same as the JSON client; any other non-2xx surfaces as `error`.
 *
 * Same server-side scope as the endpoint itself: `kinds`/`dateFrom`/
 * `dateTo` only — no `method` (see `useGetUserHistory`'s doc comment) and no
 * `cursor`/`limit` (the endpoint pages `getUserHistory` internally, capped
 * at `USER_HISTORY_CSV_ROW_CAP` rows server-side).
 */
export function useExportUserHistoryCsv(userId: string | undefined) {
  const client = useApiClient();
  const [isExporting, setIsExporting] = useState(false);
  const [isError, setIsError] = useState(false);

  const exportCsv = useCallback(
    async (filters: ExportUserHistoryCsvFilters) => {
      if (!userId) return;
      setIsExporting(true);
      setIsError(false);
      try {
        const params = new URLSearchParams();
        if (filters.kinds && filters.kinds.length > 0) params.append('kinds', filters.kinds.join(','));
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);

        const url = `${getAppConfig().apiUrl}/payments/admin/users/${userId}/history/export?${params.toString()}`;
        let idToken = loadCredentials()?.idToken ?? '';
        let response = await fetch(url, { headers: { authorization: idToken } });

        if (response.status === 401) {
          idToken = await client.forceTokenRefresh();
          response = await fetch(url, { headers: { authorization: idToken } });
        }

        if (!response.ok) {
          setIsError(true);
          return;
        }

        const csvText = await response.text();
        downloadCsvText(csvText, `historique-${userId}-${new Date().toISOString().slice(0, 10)}.csv`);
      } catch {
        setIsError(true);
      } finally {
        setIsExporting(false);
      }
    },
    [userId, client],
  );

  return { exportCsv, isExporting, isError };
}
